<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="12">
        <v-card :loading="loading" class="bg-grey-lighten-4 elevation-0">
          <v-toolbar color="primary" density="comfortable">
            <v-toolbar-title class="text-white">
              {{ title }}
            </v-toolbar-title>
            <v-spacer />
            <div class="d-flex ga-2">
              <v-tooltip location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="cancel">
                    <v-icon>mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
                Fermer
              </v-tooltip>

              <v-tooltip location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="save">
                    <v-icon>mdi-content-save-all</v-icon>
                  </v-btn>
                </template>
                Sauvegarder
              </v-tooltip>
              <v-tooltip location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="createNewPanne">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </template>
                Créer une panne
              </v-tooltip>
            </div>
          </v-toolbar>
        </v-card>
      </v-col>
    </v-row>

    <!-- PANELS -->
    <v-expansion-panels>
      <!-- INFORMATIONS -->
      <v-expansion-panel>
        <v-expansion-panel-title> Informations </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row dense>
            <v-col cols="4">
              <v-autocomplete
                v-model="product.productType"
                :items="productTypes"
                label="Type *"
              />
            </v-col>
            <v-col cols="4">
              <v-text-field v-model="product.productMake" label="Fabricant *" />
            </v-col>
            <v-col cols="4">
              <v-text-field v-model="product.aquaNb" type="number" label="N° aqua *" />
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="4">
              <v-text-field v-model="product.price" label="Prix d'achat" />
            </v-col>
            <v-col cols="4">
              <v-text-field v-model="product.model" label="Modèle *" />
            </v-col>
            <v-col cols="4">
              <v-text-field v-model="product.serialNb" label="N° Série *" />
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="4">
              <v-text-field
                v-model="product.bonEntreeDate"
                type="date"
                label="Date Bon d'entrée"
              />
            </v-col>
            <v-col cols="4">
              <v-text-field v-model="product.bonEntreeNb" label="N° Bon d'entrée" />
            </v-col>
            <v-col cols="4">
              <v-text-field v-model="product.compteur" type="number" label="Compteur" />
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="4">
              <v-text-field
                v-model="product.localisation"
                label="Localisation"
                disabled
              />
            </v-col>
            <v-col cols="4">
              <v-autocomplete
                v-model="product.productStatus"
                :items="productStatus"
                label="Status *"
              />
            </v-col>
            <v-col cols="4">
              <v-autocomplete
                v-model="product.reservationStatus"
                :items="reservationStatus"
                label="Disponibilité *"
              />
            </v-col>
          </v-row>

          <v-row dense>
            <v-col cols="4">
              <v-autocomplete
                v-model="product.laverieId"
                :items="laveries"
                item-title="numeroDeContrat"
                item-value="id"
                label="Laverie"
                clearable
              />
            </v-col>
            <v-col cols="4">
              <v-autocomplete
                v-model="product.storageDepotId"
                :items="storageDepots"
                item-title="name"
                item-value="id"
                label="Dépôt"
                clearable
              />
            </v-col>
            <v-col cols="4" v-if="isDestructedReservationStatus">
              <v-text-field
                v-model="product.destructionDate"
                type="date"
                label="Date de destruction *"
              />
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- NOTIFICATIONS -->
      <v-expansion-panel>
        <v-expansion-panel-title> Notifications </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-switch
            v-model="product.notificationEnabled"
            label="Activer les notifications"
          />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- PANNES -->
      <v-expansion-panel>
        <v-expansion-panel-title> Pannes </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-list>
            <v-list-item v-for="p in pannes" :key="p.id" class="bg-grey-lighten-2 my-2">
              <v-list-item-title class="text-blue">
                {{ p.name }}
              </v-list-item-title>
              <v-list-item-subtitle>
                Technicien: {{ p.technicien }} | Date: {{ p.date }}
              </v-list-item-subtitle>

              <template #append>
                <v-btn icon color="teal" @click="edit(p)">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon color="cyan" @click="remove(p)">
                  <v-icon>mdi-trash-can-outline</v-icon>
                </v-btn>
              </template>
            </v-list-item>
          </v-list>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <ConfirmDialog
      v-model="removePanneDialog"
      :dialog-title="dialogTitle"
      :dialog-text="dialogText"
      @confirm="onConfirm"
      @cancel="onCancel"
    />
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeMount } from "vue";
import { useRouter, useRoute } from "vue-router";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useAppStore } from "@/store/app";
import { ProductStatus, ProductType, ReservationStatus } from "@/types";
import { getProductById, postProduct, putProduct } from "@/utils/product-api";
import { deletePanne, getPanneByProduct } from "@/utils/panne-api";
import { getHistoryElementsByProduct } from "@/utils/product-history-element-api";
import { getAllLaveries } from "@/utils/laverie-api";
import { getAllStorageDepots } from "@/utils/storage-depot-api";

import type { Product } from "@/types";
import type { Panne } from "@/types";
import type { Laverie } from "@/types";
import type { StorageDepot } from "@/types";
import { HistoryElement } from "@/types";

const router = useRouter();
const route = useRoute();
const appStore = useAppStore();

const loading = ref(false);
const title = ref("");
const product = ref<Product>({} as Product);
const pannes = ref<Panne[]>([]);
const laveries = ref<Laverie[]>([]);
const storageDepots = ref<StorageDepot[]>([]);
const historyElements = ref<HistoryElement[]>([]);

const removePanneDialog = ref(false);
const selectedPanne = ref<Panne | null>(null);

const dialogTitle = "Suppression";
const dialogText = "Voulez-vous supprimer cette panne ?";

const productStatus = computed(() => Object.values(ProductStatus));
const reservationStatus = computed(() => Object.values(ReservationStatus));
const productTypes = computed(() => Object.values(ProductType));

const isCreatingNewProduct = () => route.name === "newproduct" || !route.params.id;

const isDestructedReservationStatus = computed(
  () => product.value.reservationStatus === ReservationStatus.DETRUIT
);

watch(
  () => product.value.laverieId,
  (v) => {
    if (v) {
      product.value.reservationStatus = ReservationStatus.RESERVED;
      product.value.storageDepotId = null;
    }
  }
);

watch(
  () => product.value.storageDepotId,
  (v) => {
    if (v) {
      product.value.reservationStatus = ReservationStatus.AVAILABLE;
      product.value.laverieId = null;
    }
  }
);

const save = async () => {
  loading.value = true;
  try {
    if (!product.value.id) {
      product.value = await postProduct(product.value);
      appStore.sendSuccessNotice("New product has been added.");
      router.replace({ name: "product", params: { id: product.value.id } });
      title.value = "Editer le produit";
    } else {
      await putProduct(product.value.id, product.value);
      appStore.sendSuccessNotice("Product has been updated.");
    }
    historyElements.value = await getHistoryElementsByProduct(product.value.id);
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
};

const cancel = () => router.push({ name: "products" });

const edit = (p: Panne) =>
  router.push({ name: "panne", params: { productId: product.value.id, id: p.id } });

const remove = (p: Panne) => {
  selectedPanne.value = p;
  removePanneDialog.value = true;
};

const onConfirm = async () => {
  if (!selectedPanne.value) return;
  await deletePanne(selectedPanne.value.id);
  pannes.value = await getPanneByProduct(product.value.id);
  removePanneDialog.value = false;
};

const onCancel = () => {
  removePanneDialog.value = false;
  selectedPanne.value = null;
};

const createNewPanne = () => {
  if (route.params.id) {
    router.push({ name: "newpanne", params: { productId: route.params.id } });
  }
};

onBeforeMount(() => {
  if (isCreatingNewProduct()) {
    title.value = "Nouveau produit";
    product.value.notificationEnabled = true;
    return;
  }

  title.value = "Editer le produit";
});

onMounted(async () => {
  laveries.value = await getAllLaveries();
  storageDepots.value = await getAllStorageDepots();
  const id = route.params.id as string;
  const [p, pn, he] = await Promise.all([
    getProductById(id),
    getPanneByProduct(id),
    getHistoryElementsByProduct(id),
  ]);
  product.value = p;
  pannes.value = pn;
  historyElements.value = he;
});
</script>
